import React, { PureComponent } from 'react';
import { connectRequest } from 'redux-query-react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Settings, DateTime } from 'luxon';
import Transition from 'react-transition-group/Transition';
import { HeaderText, Text } from '@el8/vital';
import Calendar from 'views/booking/Calendar/Calendar';
import { getAppointmentType } from 'modules/appointmentType';
import {
  getAvailabilitiesForAppointmentType,
  getAvailabilitiesForAppointmentTypeQuery,
} from 'modules/availability';
import {
  updateBookingInfo,
  getBookingInfo,
  selectAvailability as selectAvailabilityAction,
} from 'modules/booking';

import { State, Dispatch, ReduxQuery, BookingState } from 'types';
import { AppointmentType } from 'EntityTypes';

import styles from './Service.less';

import NextAvailabilities from './NextAvailabilities';

type OwnProps = {
  appointmentTypeId: number;
  practiceId: number;
  serviceLocationId?: number;
  physicianId?: number;
  navigate: (to: string) => void;
};

type StateProps = {
  appointmentType: AppointmentType;
  selectedPhysicianId: number;
  nextAvailabilities: { [key: string]: string[] };
  calendar: BookingState['calendar'];
};

type DispatchProps = {
  selectAvailability: (date: string, time: string) => void;
  showMoreAvailabilities: (currentMonth: string) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (
  state: State,
  { appointmentTypeId, physicianId }: OwnProps,
): StateProps => {
  const { selectedPhysicianId, calendar } = getBookingInfo(state);
  const now = DateTime.fromObject({ zone: Settings.defaultZone });
  const appointmentType = getAppointmentType(state, { appointmentTypeId });
  return {
    appointmentType,
    nextAvailabilities: getAvailabilitiesForAppointmentType(state, {
      appointmentTypeId,
      limit: 7,
      physicianId: physicianId || selectedPhysicianId,
      startDate: now.toISODate(),
      startTime: now.toFormat('HH:mm'),
    }),
    selectedPhysicianId,
    calendar,
  };
};

const mapDispatchToProps = (
  dispatch: Dispatch,
  { appointmentTypeId, navigate, practiceId, serviceLocationId, physicianId }: OwnProps,
): DispatchProps => ({
  selectAvailability: (date: string, time: string): void => {
    dispatch(
      selectAvailabilityAction(date, time, appointmentTypeId, serviceLocationId, physicianId),
    );
    navigate(`/book/${practiceId}/account`);
  },
  showMoreAvailabilities: (currentMonth): void => {
    dispatch(
      updateBookingInfo({
        calendar: {
          activeAppointmentTypeId: appointmentTypeId,
          currentMonth,
          isLoadingAvailabilities: false,
        },
      }),
    );
  },
});

const mapPropsToConfig = ({
  practiceId,
  serviceLocationId,
  appointmentTypeId,
  nextAvailabilities,
  calendar,
}: Props): ReduxQuery[] => {
  if (!nextAvailabilities) {
    return [];
  }
  let [monthStart] = Object.keys(nextAvailabilities);

  if (calendar.activeAppointmentTypeId === appointmentTypeId && calendar.currentMonth) {
    // eslint-disable-next-line prefer-destructuring
    monthStart = calendar.currentMonth;
  }

  return [
    getAvailabilitiesForAppointmentTypeQuery(practiceId, {
      appointmentTypeId,
      endDate: DateTime.fromISO(monthStart).endOf('month').toISODate(),
      force: true,
      serviceLocationId,
      startDate: monthStart,
    }),
  ];
};

class Service extends PureComponent<Props> {
  render(): JSX.Element {
    const {
      appointmentTypeId,
      appointmentType,
      calendar,
      nextAvailabilities,
      practiceId,
      selectAvailability,
      showMoreAvailabilities,
    } = this.props;
    const { name, description } = appointmentType;

    if (window.el8Globals.FEATURES.AvailabilityOnBkSite && !nextAvailabilities) {
      return null;
    }

    return (
      <div className={styles.service}>
        <HeaderText className={styles.serviceHeader} legacyVariant="legacyHeaderText" tag="h1">
          {name}
        </HeaderText>
        {description && <Text className={styles.info}>{description}</Text>}
        {nextAvailabilities ? (
          <div className={styles.availabilities}>
            <Transition
              in={calendar.activeAppointmentTypeId === appointmentTypeId}
              mountOnEnter
              unmountOnExit
              timeout={300}
            >
              {(state: string): JSX.Element => (
                <Calendar
                  appointmentTypeId={appointmentTypeId}
                  practiceId={practiceId}
                  selectAvailability={selectAvailability}
                  state={state}
                />
              )}
            </Transition>
            <Transition
              in={calendar.activeAppointmentTypeId !== appointmentTypeId}
              timeout={300}
              mountOnEnter
              unmountOnExit
            >
              {(state: string): JSX.Element => (
                <NextAvailabilities
                  availabilities={nextAvailabilities}
                  selectAvailability={selectAvailability}
                  showMoreAvailabilities={showMoreAvailabilities}
                  state={state}
                />
              )}
            </Transition>
          </div>
        ) : (
          <Text className={styles.info}>
            This service cannot be booked online at this time. Please call the office to schedule
            the appointment.
          </Text>
        )}
      </div>
    );
  }
}

export default compose<React.ComponentType<OwnProps>>(
  connect(mapStateToProps, mapDispatchToProps),
  connectRequest(mapPropsToConfig),
)(Service);
